import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  Popover,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { MRT_TableInstance } from "material-react-table";
import { useMemo, useRef, useState } from "react";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";


type CustomShowHideColumnsPopoverProps<TData extends Record<string, any>> = {
  table: MRT_TableInstance<TData>;
};

export function CustomShowHideColumnsPopover<TData extends Record<string, any>>({
                                                            table
                                                          }: CustomShowHideColumnsPopoverProps<TData>) {
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filteredColumns = useMemo(() => {
    const q = query.trim().toLowerCase();
    return table.getAllLeafColumns().filter((column) => {
      const header = (column.columnDef.header ?? "")
        .toString()
        .toLowerCase();
      const id = column.id.toLowerCase();
      if (!q) return true;
      return header.includes(q) || id.includes(q);
    });
  }, [table, query]);

  const hideAll = () => {
    table.getAllLeafColumns().forEach((c) => c.toggleVisibility(false));
  };

  const showAll = () => {
    table.getAllLeafColumns().forEach((c) => c.toggleVisibility(true));
  };

  return (
    <>
      <IconButton
        ref={anchorRef}
        onClick={() => setOpen((v) => !v)}
        size="small"
      >
        <ViewColumnIcon />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: 260,
            maxHeight: 420,
            display: "flex",
            flexDirection: "column",
            p: 1,
          },
        }}
      >

        <Box sx={{ pb: 1 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            mb={1}
          >
            <Button size="small" onClick={hideAll}>
              HIDE ALL
            </Button>
            <Button size="small" onClick={showAll}>
              SHOW ALL
            </Button>
          </Stack>

          <TextField
            size="small"
            fullWidth
            placeholder="Search columns…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </Box>

        <Divider />


        <Box sx={{ mt: 1, flex: 1, overflowY: "auto" }}>
          <List dense>
            {filteredColumns.map((column) => (
              <ListItem key={column.id} disableGutters>
                <FormControlLabel
                  sx={{ width: "100%", mx: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={column.getIsVisible()}
                      onChange={column.getToggleVisibilityHandler()}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {column.columnDef.header ?? column.id}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>
    </>
  );
}